import { useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Shield } from "lucide-react";

export const FinalCTASection = ({ onOpenModal }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [spotsAvailable, setSpotsAvailable] = useState(47);

  useEffect(() => {
    const interval = setInterval(() => {
      setSpotsAvailable((prev) => {
        if (prev > 15 && Math.random() > 0.8) {
          return prev - 1;
        }
        return prev;
      });
    }, 20000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section
      ref={ref}
      className="relative py-24 md:py-32 px-6 md:px-12 overflow-hidden"
    >
      {/* Background elements */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-t from-[#E05D3A]/10 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#E05D3A]/20 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto text-center">
        {/* Spots counter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="inline-flex items-center gap-3 bg-white/5 border border-white/10 rounded-full px-5 py-2.5 backdrop-blur-sm">
            <motion.span
              animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-2.5 h-2.5 bg-[#E05D3A] rounded-full shadow-[0_0_10px_rgba(224,93,58,0.8)]"
            />
            <span className="text-sm text-zinc-300 font-medium">
              Solo quedan <span className="text-[#E05D3A] font-bold counter-pulse">{spotsAvailable}</span> plazas en la beta
            </span>
          </div>
        </motion.div>

        {/* Headline */}
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="font-outfit text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light tracking-tight mb-6"
        >
          ¿Listo para transformar tus consultas?{" "}
          <span className="text-gradient">Empieza hoy.</span>
        </motion.h2>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-zinc-400 text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-10"
        >
          14 días gratis con todas las funciones. Sin tarjeta de crédito. Sin compromisos.
        </motion.p>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-8"
        >
          <button
            onClick={onOpenModal}
            className="group bg-[#E05D3A] text-white hover:bg-[#C85233] transition-all duration-300 shadow-[0_0_40px_rgba(224,93,58,0.5)] hover:shadow-[0_0_60px_rgba(224,93,58,0.7)] rounded-full px-10 py-5 text-lg font-medium inline-flex items-center gap-3"
            data-testid="final-cta-button"
          >
            Comenzar prueba gratuita
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>

        {/* Trust badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 text-zinc-500 text-sm"
        >
          <span className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#E05D3A]" />
            Gratis para siempre con hasta 3 clientes
          </span>
          <span className="hidden sm:inline">·</span>
          <span>Trial Pro de 14 días incluido</span>
        </motion.div>
      </div>
    </section>
  );
};
